import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { LogOut, Save, BookOpen, Edit3, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LibraryDashboard = ({ onBack }) => {
  const { user, token, logout } = useAuth();
  const [library, setLibrary] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/librarian/my-library`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : Promise.reject('Error'))
      .then(d => { setLibrary(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const startEdit = () => {
    setEditing(true);
    // Librarians CANNOT edit: official_name, library_type, municipality (admin-only)
    setEditData({
      community_name: library.community_name || '',
      email: library.email || '',
      phone: library.phone || '',
      physical_address: library.physical_address || '',
      postal_address: library.postal_address || '',
      operating_hours: library.operating_hours || '',
      website: library.website || '',
      collection_size: library.collection_size || '',
      accessibility: library.accessibility || '',
      government_services: library.government_services || '',
      other_services: library.other_services || '',
      head_librarian: library.head_librarian || '',
      opening_date: library.opening_date || '',
    });
  };

  const saveEdit = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/librarian/my-library`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(editData)
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Error'); }
      const data = await res.json();
      setLibrary(data.library);
      setMsg('Cambios guardados exitosamente');
      setEditing(false);
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px',
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px', color: '#fff', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box'
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '80px 20px', color: 'rgba(255,255,255,0.5)' }}>Cargando...</div>;
  }

  return (
    <div data-testid="librarian-dashboard" style={{ minHeight: 'calc(100vh - 200px)', color: '#fff' }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px', marginBottom: '20px',
        background: 'rgba(0,0,0,0.2)', borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <BookOpen size={24} color="#3498db" />
          <div>
            <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Mi Biblioteca</h1>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{user?.email}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onBack} style={{
            padding: '8px 14px', background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
            color: '#fff', cursor: 'pointer', fontSize: '13px'
          }}>Volver</button>
          <button data-testid="librarian-logout-btn" onClick={logout} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px', background: 'rgba(233,69,96,0.2)',
            border: '1px solid rgba(233,69,96,0.3)', borderRadius: '8px',
            color: '#e94560', cursor: 'pointer', fontSize: '13px'
          }}><LogOut size={16} /> Cerrar Sesión</button>
        </div>
      </div>

      {msg && (
        <div data-testid="librarian-message" style={{
          padding: '10px 16px', marginBottom: '14px', borderRadius: '8px',
          background: msg.startsWith('Error') ? 'rgba(233,69,96,0.15)' : 'rgba(46,204,113,0.15)',
          border: `1px solid ${msg.startsWith('Error') ? 'rgba(233,69,96,0.3)' : 'rgba(46,204,113,0.3)'}`,
          color: msg.startsWith('Error') ? '#e94560' : '#2ecc71', fontSize: '14px'
        }}>{msg}</div>
      )}

      {library ? (
        <div style={{
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px', overflow: 'hidden'
        }}>
          <div style={{
            padding: '18px 22px', background: 'rgba(52,152,219,0.1)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <div>
              <h2 style={{ margin: '0 0 4px', fontSize: '20px' }}>{library.official_name}</h2>
              <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
                {library.municipality} | {library.library_type}
              </span>
            </div>
            {!editing && (
              <button data-testid="librarian-edit-btn" onClick={startEdit} style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 16px', background: 'rgba(52,152,219,0.2)',
                border: '1px solid rgba(52,152,219,0.3)', borderRadius: '8px',
                color: '#3498db', cursor: 'pointer', fontSize: '13px'
              }}><Edit3 size={16} /> Editar</button>
            )}
          </div>

          <div style={{ padding: '18px 22px' }}>
            {editing ? (
              <div style={{ display: 'grid', gap: '14px' }}>
                {/* Read-only fields (admin-only) */}
                <div style={{
                  padding: '12px 14px', background: 'rgba(255,255,255,0.03)',
                  borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)',
                  marginBottom: '4px'
                }}>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Campos no editables (solo admin)
                  </div>
                  {[
                    ['Nombre oficial', library.official_name],
                    ['Tipo de biblioteca', library.library_type],
                    ['Municipio', library.municipality],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: 'flex', gap: '8px', marginBottom: '4px', fontSize: '13px' }}>
                      <span style={{ color: 'rgba(255,255,255,0.4)' }}>{label}:</span>
                      <span style={{ color: 'rgba(255,255,255,0.65)' }}>{val}</span>
                    </div>
                  ))}
                </div>

                {/* Editable fields */}
                {Object.entries(editData).map(([key, val]) => (
                  <div key={key}>
                    <label style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: '5px', display: 'block' }}>
                      {key.replace(/_/g, ' ')}
                    </label>
                    {val && val.length > 60 ? (
                      <textarea value={val} onChange={e => setEditData(p => ({ ...p, [key]: e.target.value }))}
                        rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                    ) : (
                      <input value={val} onChange={e => setEditData(p => ({ ...p, [key]: e.target.value }))}
                        style={inputStyle} />
                    )}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  <button data-testid="librarian-save-btn" onClick={saveEdit} disabled={saving}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '12px 24px', background: 'linear-gradient(135deg, #2ecc71, #27ae60)',
                      border: 'none', borderRadius: '8px', color: '#fff',
                      cursor: 'pointer', fontSize: '14px', fontWeight: 600
                    }}><Save size={16} /> {saving ? 'Guardando...' : 'Guardar'}</button>
                  <button onClick={() => setEditing(false)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '12px 24px', background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
                      color: '#fff', cursor: 'pointer', fontSize: '14px'
                    }}><X size={16} /> Cancelar</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '10px' }}>
                {[
                  ['Nombre oficial', library.official_name],
                  ['Conocida como', library.community_name],
                  ['Email', library.email],
                  ['Teléfono', library.phone],
                  ['Dirección física', library.physical_address],
                  ['Dirección postal', library.postal_address],
                  ['Horario', library.operating_hours],
                  ['Sitio web', library.website],
                  ['Tamaño de colección', library.collection_size],
                  ['Accesibilidad', library.accessibility],
                  ['Servicios gubernamentales', library.government_services],
                  ['Otros servicios', library.other_services],
                  ['Bibliotecario a cargo', library.head_librarian],
                  ['Fecha de apertura', library.opening_date],
                  ['Red / sistema', library.network_name],
                ].filter(([, v]) => v && v.toUpperCase() !== 'N/A').map(([label, val]) => (
                  <div key={label} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '3px' }}>{label}</div>
                    <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.85)', wordBreak: 'break-word' }}>{val}</div>
                  </div>
                ))}

                {library.services && library.services.length > 0 && (
                  <div style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '3px' }}>Servicios</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {library.services.map((s, i) => (
                        <span key={i} style={{
                          padding: '3px 8px', background: 'rgba(52,152,219,0.15)',
                          borderRadius: '10px', fontSize: '12px', color: 'rgba(255,255,255,0.7)'
                        }}>{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                {library.resources && library.resources.length > 0 && (
                  <div style={{ padding: '6px 0' }}>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '3px' }}>Recursos</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {library.resources.map((r, i) => (
                        <span key={i} style={{
                          padding: '3px 8px', background: 'rgba(46,204,113,0.15)',
                          borderRadius: '10px', fontSize: '12px', color: 'rgba(255,255,255,0.7)'
                        }}>{r}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(255,255,255,0.5)' }}>
          No se encontró la biblioteca asociada a tu cuenta.
        </div>
      )}
    </div>
  );
};

export default LibraryDashboard;
